/** Matches a full `#RRGGBB` hex color. Short (`#RGB`) form is not accepted. */
export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/

export interface Hsv {
    /** Hue, 0–360 */
    h: number
    /** Saturation, 0–1 */
    s: number
    /** Value / brightness, 0–1 */
    v: number
}

export function isHexColor(value: string): boolean {
    return HEX_COLOR_PATTERN.test(value)
}

/**
 * Strips everything that is not a hex digit, caps the result at six digits and
 * re-adds the leading `#`, so pasting `#1e88e5`, typing `1E88E5` and typing
 * junk all converge on the same normalized value. An empty result stays empty —
 * that is the "unset" state, not `#000000`.
 */
export function normalizeHexInput(raw: string): string {
    const digits = raw.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6)
    return digits ? `#${digits.toUpperCase()}` : ''
}

const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value))

export function hexToHsv(hex: string): Hsv {
    if (!isHexColor(hex)) return { h: 0, s: 0, v: 1 }

    const r = parseInt(hex.slice(1, 3), 16) / 255
    const g = parseInt(hex.slice(3, 5), 16) / 255
    const b = parseInt(hex.slice(5, 7), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min

    let h = 0
    if (delta !== 0) {
        if (max === r) h = ((g - b) / delta) % 6
        else if (max === g) h = (b - r) / delta + 2
        else h = (r - g) / delta + 4
        h *= 60
        if (h < 0) h += 360
    }

    return { h, s: max === 0 ? 0 : delta / max, v: max }
}

export function hsvToHex({ h, s, v }: Hsv): string {
    const hue = ((h % 360) + 360) % 360
    const sat = clamp(s, 0, 1)
    const val = clamp(v, 0, 1)

    const c = val * sat
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
    const m = val - c

    const [r, g, b] =
        hue < 60
            ? [c, x, 0]
            : hue < 120
              ? [x, c, 0]
              : hue < 180
                ? [0, c, x]
                : hue < 240
                  ? [0, x, c]
                  : hue < 300
                    ? [x, 0, c]
                    : [c, 0, x]

    const toHex = (channel: number): string =>
        Math.round((channel + m) * 255)
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Picks a readable foreground for a background color, so the picker's thumb and
 * any text on a swatch stay visible on both very light and very dark colors.
 * Uses the WCAG relative-luminance formula.
 */
export function getContrastingColor(hex: string): '#000000' | '#FFFFFF' {
    if (!isHexColor(hex)) return '#000000'

    const channel = (offset: number): number => {
        const value = parseInt(hex.slice(offset, offset + 2), 16) / 255
        return value <= 0.03928
            ? value / 12.92
            : Math.pow((value + 0.055) / 1.055, 2.4)
    }

    const luminance =
        0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)

    return luminance > 0.179 ? '#000000' : '#FFFFFF'
}

// --- OKLab / OKLCH ---------------------------------------------------------
// Björn Ottosson's OKLab. Used by the website's theme derivation, which pins
// lightness and chroma per token and varies only the hue, so the ramp keeps its
// text contrast whatever seed color an editor picks.

export interface Oklch {
    /** Perceptual lightness, 0–1 */
    l: number
    /** Chroma, roughly 0–0.4 for sRGB */
    c: number
    /** Hue angle in degrees, 0–360 */
    h: number
}

const srgbToLinear = (channel: number): number =>
    channel <= 0.04045
        ? channel / 12.92
        : Math.pow((channel + 0.055) / 1.055, 2.4)

const linearToSrgb = (channel: number): number =>
    channel <= 0.0031308
        ? 12.92 * channel
        : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055

export function hexToOklch(hex: string): Oklch | null {
    if (!isHexColor(hex)) return null

    const r = srgbToLinear(parseInt(hex.slice(1, 3), 16) / 255)
    const g = srgbToLinear(parseInt(hex.slice(3, 5), 16) / 255)
    const b = srgbToLinear(parseInt(hex.slice(5, 7), 16) / 255)

    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

    const okL = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
    const okA = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
    const okB = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s

    const c = Math.sqrt(okA * okA + okB * okB)
    let h = (Math.atan2(okB, okA) * 180) / Math.PI
    if (h < 0) h += 360

    return { l: okL, c, h }
}

/** Converts OKLCH to sRGB channels in 0–1, without clamping. */
function oklchToLinearRgb({ l, c, h }: Oklch): [number, number, number] {
    const rad = (h * Math.PI) / 180
    const okA = c * Math.cos(rad)
    const okB = c * Math.sin(rad)

    const lCube = (l + 0.3963377774 * okA + 0.2158037573 * okB) ** 3
    const mCube = (l - 0.1055613458 * okA - 0.0638541728 * okB) ** 3
    const sCube = (l - 0.0894841775 * okA - 1.291485548 * okB) ** 3

    return [
        4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube,
        -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube,
        -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube,
    ]
}

const isInGamut = (channels: [number, number, number]): boolean =>
    channels.every((channel) => channel >= -0.0001 && channel <= 1.0001)

/** How much chroma is shed on each gamut-mapping step. */
const GAMUT_CHROMA_STEP = 0.08
const GAMUT_MAX_STEPS = 32

/**
 * Converts OKLCH to a hex string, walking chroma down in 8% steps (up to 32)
 * until the color fits in sRGB. Lightness and hue are never touched — lightness
 * carries the contrast and hue carries the brand.
 */
export function oklchToHex({ l, c, h }: Oklch): string {
    let chroma = c
    let channels = oklchToLinearRgb({ l, c: chroma, h })

    for (let step = 0; step < GAMUT_MAX_STEPS && !isInGamut(channels); step++) {
        chroma *= 1 - GAMUT_CHROMA_STEP
        channels = oklchToLinearRgb({ l, c: chroma, h })
    }

    const toHex = (channel: number): string =>
        Math.round(Math.min(1, Math.max(0, linearToSrgb(channel))) * 255)
            .toString(16)
            .padStart(2, '0')
            .toUpperCase()

    return `#${toHex(channels[0])}${toHex(channels[1])}${toHex(channels[2])}`
}
